'use client'

import { useEffect, useRef, useState } from 'react'
import Image from 'next/image'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { isPreSizedImage } from '@/lib/feedbridge/helpers'

/**
 * Listing-card thumbnail slider (Asana 022): the FIRST image loads with the
 * page; the remaining preview images (capped at 3 total by the callers) only
 * start downloading once the page has finished loading — the lifestylegroup
 * pattern — so the grid render costs nothing extra. Arrows slide between
 * images without leaving the card; the card itself remains one big link, so
 * arrow taps preventDefault/stopPropagation to stay on the page.
 */
export function CardImageSlider({
  images,
  alt,
  alwaysShowControls = false,
}: {
  images: string[]
  alt: string
  /** Render the arrows even before the extra previews arrive (Steve: controls
   *  visible from first paint — the enriched stage adds the images in place) */
  alwaysShowControls?: boolean
}) {
  const [index, setIndex] = useState(0)
  // Whether the non-primary previews may load (true once the page is idle)
  const [previewsActive, setPreviewsActive] = useState(false)
  const [errored, setErrored] = useState<Record<number, boolean>>({})

  useEffect(() => {
    let cancelled = false
    const activate = () => {
      if (!cancelled) setPreviewsActive(true)
    }
    if (document.readyState === 'complete') {
      // Already loaded — wait for an idle moment
      if ('requestIdleCallback' in window) {
        const id = window.requestIdleCallback(activate, { timeout: 3000 })
        return () => {
          cancelled = true
          window.cancelIdleCallback(id)
        }
      }
      const t = setTimeout(activate, 800)
      return () => {
        cancelled = true
        clearTimeout(t)
      }
    }
    window.addEventListener('load', activate, { once: true })
    // Safety net if the load event never fires (long video downloads etc.)
    const t = setTimeout(activate, 6000)
    return () => {
      cancelled = true
      clearTimeout(t)
      window.removeEventListener('load', activate)
    }
  }, [])

  const count = images.length
  const go = (delta: number) => setIndex((i) => (i + delta + count) % count)

  // Swipe on touch devices (Steve 2026-08-21). The card is one big link, so a
  // swipe must not also open the property: `swiped` blocks the click that
  // follows the gesture. Vertical drags are left alone so the page still
  // scrolls normally through the grid.
  const touch = useRef<{ x: number; y: number; swiped: boolean } | null>(null)
  const SWIPE_MIN_PX = 40

  const onTouchStart = (e: React.TouchEvent) => {
    const point = e.touches[0]
    if (!point) return
    touch.current = { x: point.clientX, y: point.clientY, swiped: false }
  }

  const onTouchMove = (e: React.TouchEvent) => {
    const start = touch.current
    const point = e.touches[0]
    if (!start || !point) return
    const dx = point.clientX - start.x
    const dy = point.clientY - start.y
    if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) start.swiped = true
  }

  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touch.current
    const point = e.changedTouches[0]
    if (!start || !point) return
    const dx = point.clientX - start.x
    const dy = point.clientY - start.y
    if (Math.abs(dx) >= SWIPE_MIN_PX && Math.abs(dx) > Math.abs(dy)) {
      setPreviewsActive(true)
      go(dx < 0 ? 1 : -1)
      start.swiped = true
    }
  }

  // A gesture ends in a click on the underlying card link — swallow it.
  const onClickCapture = (e: React.MouseEvent) => {
    if (touch.current?.swiped) {
      e.preventDefault()
      e.stopPropagation()
      touch.current.swiped = false
    }
  }

  const arrow = (delta: number, label: string, Icon: typeof ChevronLeft, side: string) => (
    <button
      type="button"
      aria-label={label}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        setPreviewsActive(true) // user asked for previews — load them now
        go(delta)
      }}
      className={`absolute top-1/2 ${side} z-[2] flex size-5 -translate-y-1/2 items-center justify-center rounded-sm bg-accent text-white shadow-xs hover:bg-accent/90 lg:opacity-0 lg:group-hover:opacity-100 lg:focus-visible:opacity-100`}
    >
      <Icon className="size-3.5" aria-hidden="true" />
    </button>
  )

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onClickCapture={onClickCapture}
      // Let the browser keep vertical scrolling; we handle the horizontal axis.
      style={{ touchAction: 'pan-y' }}
    >
      {/* Sliding track — one viewport-width panel per image */}
      <div
        className="flex h-full transition-transform duration-400 ease-in-out"
        style={{ width: `${count * 100}%`, transform: `translateX(-${(index * 100) / count}%)` }}
      >
        {images.map((src, i) => (
          <div key={src} className="relative h-full" style={{ width: `${100 / count}%` }}>
            {errored[i]
              ? null
              : (i === 0 || previewsActive || i === index) && (
                  <Image
                    src={src}
                    alt={i === 0 ? alt : ''}
                    fill
                    quality={60}
                    loading={i === 0 ? undefined : 'lazy'}
                    className="object-cover"
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    // Pre-resized CDN variants (…_800.webp) are already WebP at card
                    // size — serve them straight through so an arrow "page turn" isn't
                    // waiting on next/image to re-encode them (Steve 2026-08-23).
                    // Full-res originals still go through the optimiser.
                    unoptimized={isPreSizedImage(src)}
                    onError={() => setErrored((p) => ({ ...p, [i]: true }))}
                  />
                )}
          </div>
        ))}
      </div>

      {(count > 1 || alwaysShowControls) && (
        <>
          {arrow(-1, 'Previous photo', ChevronLeft, 'left-2')}
          {arrow(1, 'Next photo', ChevronRight, 'right-2')}
          {/* Position dots — hover-gated with the IDENTICAL classes as the arrows so
              both appear in the same instant (Steve 2026-08-19: dots were always
              visible / out of sync with the arrows). Colors still transition; opacity
              deliberately doesn't, matching the arrows' instant reveal. */}
          {count > 1 && (
            <div className="pointer-events-none absolute inset-x-0 bottom-2 z-[2] flex justify-center gap-1.5 lg:opacity-0 lg:group-hover:opacity-100">
              {images.map((_, i) => (
                <span
                  key={i}
                  className={`size-1.5 rounded-full transition-colors ${i === index ? 'bg-background' : 'bg-background/50'}`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}
